import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const buildings = [
  {
    name: "Lazaridis Hall",
    image: "/buildings/lazaridis-hall.jpg",
    count: "40+ Rooms",
    color: "from-blue-500/40 to-purple-500/40"
  },
  {
    name: "Science Building",
    image: "/buildings/science-building.jpg",
    count: "25+ Rooms",
    color: "from-emerald-500/40 to-teal-500/40"
  },
  {
    name: "University Library",
    image: "https://www.vreng.ca/uploads/6/1/2/0/61206667/photo-5-22_3_orig.jpg",
    count: "Study Zones",
    color: "from-orange-500/40 to-red-500/40"
  }
];

const QuickAccess = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              Popular <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">Spots</span>
            </h2>
            <p className="text-lg text-gray-400">Most frequented locations on campus</p>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onClick={() => navigate('/search-page')}
            className="hidden md:flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.05] hover:bg-white/[0.1] text-white transition-all group border border-white/5 hover:border-white/10"
          >
            View all locations 
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {buildings.map((building, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              onClick={() => navigate(`/building/${encodeURIComponent(building.name)}`)}
              className="group relative aspect-[3/4] overflow-hidden rounded-[2rem] cursor-pointer"
            >
              <img
                src={building.image}
                alt={building.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              
              {/* Gradient overlays */}
              <div className={`absolute inset-0 bg-gradient-to-t ${building.color} mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out">
                  <span className="inline-block px-3 py-1 mb-4 text-xs font-medium tracking-wider text-white uppercase bg-white/20 backdrop-blur-md rounded-full border border-white/10">
                    {building.count}
                  </span>
                  <h3 className="text-3xl font-bold text-white mb-2">{building.name}</h3>
                  
                  <div className="h-0 group-hover:h-8 overflow-hidden transition-all duration-500 opacity-0 group-hover:opacity-100">
                    <div className="flex items-center gap-2 text-white/80 text-sm pt-2">
                      <span>Explore building</span>
                      <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.button
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          onClick={() => navigate('/search-page')}
          className="md:hidden w-full mt-12 py-4 rounded-2xl bg-white/5 border border-white/10 text-white flex items-center justify-center gap-2 font-medium active:bg-white/10"
        >
          View all locations <ArrowRight size={18} />
        </motion.button>
      </div>
    </section>
  );
};

export default QuickAccess;